import { useId } from 'react';

import { Icon } from '@/design-system';
import { COST_KEYS, type CostAmounts, type PublicLanguage } from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';
import { formatAmount } from '../format';
import { isModifiedClick } from '../jump';
import type { CostTable as CostTableData } from '../reportIndex';
import { chapterAnchor, costRowId } from '../useReportView';
import { SectionTitle } from './SectionTitle';

export interface CostTableProps {
    table: CostTableData;
    language: PublicLanguage;
    copy: PublicCopy;
    headingLevel: 2 | 3;
    /** Naar dit onderdeel in het rapport. */
    onJump: (key: string) => void;
}

/**
 * Wat elk onderdeel kost, op het tabblad Kosten: Direct, Op termijn en
 * Verbetering naast elkaar, met onderaan per kolom het totaal. Een tik op een
 * regel opent dat onderdeel in het rapport, met de bevindingen achter de bedragen.
 */
export function CostTable({ table, language, copy, headingLevel, onJump }: CostTableProps) {
    const titleId = useId();

    return (
        <section
            aria-labelledby={titleId}
            className="border-b border-[var(--border-subtle)] px-[var(--pr-pad-x)] py-6"
        >
            <SectionTitle level={headingLevel} id={titleId} description={copy.costTableIntro}>
                {copy.costTableTitle}
            </SectionTitle>

            <CostGrid
                labelledBy={titleId}
                firstColumn={copy.costsColumnPart}
                rows={table.rows}
                total={{ label: copy.costsTotalRow, amounts: table.total }}
                language={language}
                copy={copy}
                onJump={onJump}
            />
        </section>
    );
}

export interface CostGridRow {
    key: string;
    number: string | null;
    title: string;
    amounts: CostAmounts;
}

/**
 * Het raster van elke regel. Breed staan naam en drie bedragen naast elkaar;
 * smal staat de naam op de eerste regel en staan de bedragen eronder, in
 * dezelfde drie kolommen als de kop. Zo past de tabel op een telefoon, zonder
 * zijwaarts te schuiven.
 */
const grid =
    'grid grid-cols-3 gap-x-3 @min-[560px]:grid-cols-[minmax(0,1fr)_repeat(3,minmax(84px,108px))_16px]';

const amountCell =
    'text-right font-[family-name:var(--font-mono)] text-[13px] leading-[1.35] whitespace-nowrap text-[var(--text-primary)]';

/**
 * Een tabel met de drie kostensoorten: een tabel voor een schermlezer
 * (`role="table"`, kolom- en rijkoppen), een rustige lijst met dunne lijnen
 * voor het oog. Een bedrag van nul is een streep, zodat de bedragen die er wel
 * zijn eruit springen.
 *
 * Met `onJump` is elke regel een link naar zijn onderdeel in het rapport, en de
 * hele regel (minstens 44 px) is waar je tikt.
 */
export function CostGrid({
    labelledBy,
    label,
    firstColumn,
    rows,
    total,
    language,
    copy,
    onJump,
}: {
    /** Het `id` van de kop die de tabel een naam geeft; anders `label`. */
    labelledBy?: string;
    label?: string;
    firstColumn: string;
    rows: CostGridRow[];
    total: { label: string; amounts: CostAmounts } | null;
    language: PublicLanguage;
    copy: PublicCopy;
    onJump?: (key: string) => void;
}) {
    const money = (value: number, strong = false) =>
        value === 0 ? (
            <>
                <span aria-hidden="true" className="text-[var(--text-muted-mono-safe)]">
                    –
                </span>
                <span className="sr-only">{formatAmount(0, language)}</span>
            </>
        ) : (
            <span className={strong ? 'font-semibold' : undefined}>
                {formatAmount(value, language)}
            </span>
        );

    return (
        <div
            role="table"
            {...(labelledBy ? { 'aria-labelledby': labelledBy } : { 'aria-label': label })}
            className="-mx-2"
        >
            <div role="rowgroup">
                <div
                    role="row"
                    className={cn(grid, 'px-2 pb-2 text-[12px] leading-[1.35] font-medium')}
                >
                    <span
                        role="columnheader"
                        className="sr-only text-left text-[var(--text-tertiary-safe)] @min-[560px]:not-sr-only"
                    >
                        {firstColumn}
                    </span>
                    {COST_KEYS.map((key) => (
                        <span
                            key={key}
                            role="columnheader"
                            className="text-right text-[var(--text-tertiary-safe)]"
                        >
                            {copy.costLabels[key]}
                        </span>
                    ))}
                </div>
            </div>

            <div role="rowgroup">
                {rows.map((row) => {
                    const quiet = COST_KEYS.every((key) => row.amounts[key] === 0);
                    const titleStyle = cn(
                        'inline-flex items-baseline gap-2 text-[14px] leading-[1.35] text-pretty',
                        quiet
                            ? 'text-[var(--text-secondary)]'
                            : 'font-medium text-[var(--text-primary)]',
                    );
                    const title = (
                        <>
                            {row.number ? (
                                <span className="min-w-[1.75rem] shrink-0 font-[family-name:var(--font-mono)] text-[12px] font-normal text-[var(--text-muted-mono-safe)]">
                                    {row.number}
                                </span>
                            ) : null}
                            <span className="min-w-0 break-words">{row.title}</span>
                        </>
                    );

                    return (
                        <div
                            key={row.key}
                            role="row"
                            {...(onJump ? { id: costRowId(row.key), 'data-jump-row': '' } : {})}
                            className={cn(
                                grid,
                                'relative min-h-[44px] content-center items-baseline gap-y-1 border-t border-[var(--border-subtle-2)] px-2 py-[10px]',
                                onJump &&
                                    'scroll-mt-[64px] transition-colors duration-100 has-[a:hover]:bg-[var(--surface-row-hover)] data-[jumped]:bg-[var(--accent-tint)]',
                                onJump &&
                                    'has-[a:focus-visible]:outline-2 has-[a:focus-visible]:-outline-offset-2 has-[a:focus-visible]:outline-[var(--accent)]',
                            )}
                        >
                            <span
                                role="rowheader"
                                className="col-span-3 min-w-0 @min-[560px]:col-span-1"
                            >
                                {onJump ? (
                                    <a
                                        href={`#${chapterAnchor(row.key)}`}
                                        onClick={(event) => {
                                            if (isModifiedClick(event)) {
                                                return;
                                            }

                                            event.preventDefault();
                                            onJump(row.key);
                                        }}
                                        className={cn(
                                            titleStyle,
                                            'no-underline outline-none',
                                            /* De hele regel is de link: een laag eroverheen. */
                                            "after:absolute after:inset-0 after:content-['']",
                                        )}
                                    >
                                        {title}
                                    </a>
                                ) : (
                                    <span className={titleStyle}>{title}</span>
                                )}
                            </span>
                            {COST_KEYS.map((key) => (
                                <span
                                    key={key}
                                    role="cell"
                                    /* Smal zegt een regel zonder kosten niets met drie strepen; alleen voorgelezen. */
                                    className={cn(amountCell, quiet && '@max-[560px]:sr-only')}
                                >
                                    {money(row.amounts[key])}
                                </span>
                            ))}
                            {onJump ? (
                                <Icon
                                    name="chevronDown"
                                    size="sm"
                                    tone="muted"
                                    className="hidden -rotate-90 self-center @min-[560px]:block"
                                />
                            ) : null}
                        </div>
                    );
                })}
            </div>

            {total ? (
                <div role="rowgroup">
                    <div
                        role="row"
                        className={cn(
                            grid,
                            'gap-y-1 border-t border-[var(--border-default)] px-2 pt-3 pb-1',
                        )}
                    >
                        <span
                            role="rowheader"
                            className="col-span-3 text-[14px] leading-[1.35] font-semibold text-[var(--text-primary)] @min-[560px]:col-span-1"
                        >
                            {total.label}
                        </span>
                        {COST_KEYS.map((key) => (
                            <span key={key} role="cell" className={amountCell}>
                                {money(total.amounts[key], true)}
                            </span>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
